import ExcelJS from 'exceljs';
import type {
  CreateVenueBulkItemResult,
  VenueOpeningHoursData,
} from '../../../shared/dto/EntityRepository';
import type {
  CreateVenueBulkItemReq,
  CreateVenueReq,
} from '../../../shared/dto/ReqDtoRepository';
import {
  DayOfWeek,
  VenueAdmissionType,
  VenueType,
} from '../../../shared/enum/EnumRepository';

const HEADER_MAP: Record<string, keyof CreateVenueReq> = {
  venuecode: 'venueCode',
  code: 'venueCode',
  코드: 'venueCode',
  name: 'name',
  이름: 'name',
  nameen: 'nameEn',
  영문명: 'nameEn',
  type: 'type',
  유형: 'type',
  countrycode: 'countryCode',
  country: 'countryCode',
  국가: 'countryCode',
  city: 'city',
  도시: 'city',
  district: 'district',
  구: 'district',
  venuetag: 'venueTag',
  venuetagnames: 'venueTag',
  지역태그: 'venueTag',
  지역: 'venueTag',
  roadaddress: 'roadAddress',
  address: 'roadAddress',
  주소: 'roadAddress',
  roadaddressen: 'roadAddressEn',
  addressen: 'roadAddressEn',
  영문주소: 'roadAddressEn',
  latitude: 'latitude',
  lat: 'latitude',
  위도: 'latitude',
  longitude: 'longitude',
  lng: 'longitude',
  lon: 'longitude',
  경도: 'longitude',
  timezone: 'timeZone',
  tz: 'timeZone',
  타임존: 'timeZone',
  instagramurl: 'instagramUrl',
  instagram: 'instagramUrl',
  인스타그램: 'instagramUrl',
  websiteurl: 'websiteUrl',
  website: 'websiteUrl',
  홈페이지: 'websiteUrl',
  웹사이트: 'websiteUrl',
  admissiontype: 'admissionType',
  입장유형: 'admissionType',
  phonenumber: 'phoneNumber',
  phone: 'phoneNumber',
  tel: 'phoneNumber',
  전화: 'phoneNumber',
  description: 'description',
  설명: 'description',
  descriptionen: 'descriptionEn',
  영문설명: 'descriptionEn',
  navermapurl: 'naverMapUrl',
  navermap: 'naverMapUrl',
  네이버맵: 'naverMapUrl',
  googlemapurl: 'googleMapUrl',
  googlemap: 'googleMapUrl',
  구글맵: 'googleMapUrl',
  admissionnote: 'admissionNote',
  입장메모: 'admissionNote',
  openinghoursnote: 'openingHoursNote',
  운영시간메모: 'openingHoursNote',
  openinghours: 'openingHours',
  운영시간: 'openingHours',
};

const REQUIRED_HEADERS: Array<keyof CreateVenueReq> = [
  'venueCode',
  'name',
  'type',
  'countryCode',
  'city',
  'roadAddress',
  'timeZone',
];

const URL_FIELDS = new Set<keyof CreateVenueReq>([
  'instagramUrl',
  'websiteUrl',
  'naverMapUrl',
  'googleMapUrl',
]);

type OpeningHoursColumn =
  | 'venueCode'
  | 'dayOfWeek'
  | 'closed'
  | 'opensAt'
  | 'closesAt';

const OPENING_HOURS_HEADER_MAP: Record<string, OpeningHoursColumn> = {
  venuecode: 'venueCode',
  code: 'venueCode',
  코드: 'venueCode',
  dayofweek: 'dayOfWeek',
  day: 'dayOfWeek',
  요일: 'dayOfWeek',
  closed: 'closed',
  isclosed: 'closed',
  휴무: 'closed',
  opensat: 'opensAt',
  openingtime: 'opensAt',
  시작시간: 'opensAt',
  오픈시간: 'opensAt',
  closesat: 'closesAt',
  closingtime: 'closesAt',
  종료시간: 'closesAt',
  마감시간: 'closesAt',
};

const REQUIRED_OPENING_HOURS_HEADERS: OpeningHoursColumn[] = [
  'venueCode',
  'dayOfWeek',
  'closed',
  'opensAt',
  'closesAt',
];

export type VenueBulkSheetParseErrorCode =
  | 'NO_SHEET'
  | 'UNKNOWN_HEADER'
  | 'BLANK_HEADER_WITH_DATA'
  | 'DUPLICATE_HEADER'
  | 'MISSING_REQUIRED_HEADER'
  | 'UNKNOWN_VENUE_CODE';

export class VenueBulkSheetParseError extends Error {
  constructor(
    public readonly code: VenueBulkSheetParseErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'VenueBulkSheetParseError';
  }
}

export interface VenueBulkSheetParseResult {
  venueList: CreateVenueBulkItemReq[];
  invalidResults: CreateVenueBulkItemResult[];
  totalCount: number;
}

const normalizeHeader = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[\s_-]+/g, '');

const cellToString = (
  value: ExcelJS.CellValue,
  preferHyperlink = false,
): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    if ('hyperlink' in value) {
      const link = value as { text?: string; hyperlink?: string };
      const selected = preferHyperlink
        ? link.hyperlink || link.text
        : link.text || link.hyperlink;
      return (selected ?? '').trim();
    }
    if ('result' in value) {
      return cellToString(value.result as ExcelJS.CellValue, preferHyperlink);
    }
    if ('text' in value && typeof value.text === 'string') {
      return value.text.trim();
    }
    if ('richText' in value && Array.isArray(value.richText)) {
      return value.richText
        .map((part: { text?: string }) => part.text ?? '')
        .join('')
        .trim();
    }
  }
  return '';
};

const hasCellData = (value: ExcelJS.CellValue): boolean => {
  if (value == null) return false;
  if (typeof value === 'object'
    && ('formula' in value || 'sharedFormula' in value || 'error' in value)) {
    return true;
  }
  return cellToString(value, true) !== '';
};

const isNumericCell = (value: ExcelJS.CellValue): boolean => {
  if (typeof value === 'number') return true;
  return value != null
    && typeof value === 'object'
    && 'result' in value
    && typeof value.result === 'number';
};

const getCellError = (value: ExcelJS.CellValue): string | null => {
  if (value == null || value instanceof Date || typeof value !== 'object') {
    return null;
  }
  if ('error' in value) return String(value.error);
  if ('result' in value) {
    if (value.result == null) return 'formula has no cached result';
    return getCellError(value.result as ExcelJS.CellValue);
  }
  if ('formula' in value || 'sharedFormula' in value) {
    return 'formula has no cached result';
  }
  return null;
};

const getColumnKeys = <T extends string>(
  sheet: ExcelJS.Worksheet,
  headerMap: Record<string, T>,
  requiredHeaders: T[],
): { columnKeys: Array<T | null>; colCount: number } => {
  const headerRow = sheet.getRow(1);
  const colCount = Math.max(sheet.columnCount, headerRow.cellCount);
  const columnKeys: Array<T | null> = [];
  const mappedHeaders = new Set<T>();

  for (let col = 1; col <= colCount; col += 1) {
    const rawHeader = cellToString(headerRow.getCell(col).value);
    if (!rawHeader) {
      let hasData = false;
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1 && hasCellData(row.getCell(col).value)) hasData = true;
      });
      if (hasData) {
        throw new VenueBulkSheetParseError(
          'BLANK_HEADER_WITH_DATA',
          `${sheet.name} column ${col} contains data but has no header.`,
        );
      }
      columnKeys[col] = null;
      continue;
    }

    const mapped = headerMap[normalizeHeader(rawHeader)];
    if (!mapped) {
      throw new VenueBulkSheetParseError(
        'UNKNOWN_HEADER',
        `Unknown header "${rawHeader}" in ${sheet.name} column ${col}.`,
      );
    }
    if (mappedHeaders.has(mapped)) {
      throw new VenueBulkSheetParseError(
        'DUPLICATE_HEADER',
        `Header "${rawHeader}" duplicates ${mapped} in ${sheet.name}.`,
      );
    }
    mappedHeaders.add(mapped);
    columnKeys[col] = mapped;
  }

  const missingHeaders = requiredHeaders.filter((key) => !mappedHeaders.has(key));
  if (missingHeaders.length > 0) {
    throw new VenueBulkSheetParseError(
      'MISSING_REQUIRED_HEADER',
      `Missing required headers in ${sheet.name}: ${missingHeaders.join(', ')}.`,
    );
  }

  return { columnKeys, colCount };
};

const toEnumToken = (raw: string): string =>
  raw.trim().toUpperCase().replace(/\s+/g, '_');

const normalizeVenueCode = (raw: string): string => raw.trim().toUpperCase();

const parseClosed = (value: ExcelJS.CellValue): boolean | undefined => {
  const token = cellToString(value).trim().toUpperCase();
  if (['TRUE', 'YES', 'Y', '1', 'CLOSED', '휴무'].includes(token)) return true;
  if (['FALSE', 'NO', 'N', '0', 'OPEN', '영업'].includes(token)) return false;
  return undefined;
};

interface ParsedTime {
  value?: string;
  seconds?: number;
  error?: string;
}

const timeFromSeconds = (totalSeconds: number): ParsedTime => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const value = [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, '0'))
    .slice(0, seconds === 0 ? 2 : 3)
    .join(':');
  return { value, seconds: totalSeconds };
};

const parseTime = (value: ExcelJS.CellValue): ParsedTime => {
  if (value == null || value === '') return {};
  if (value instanceof Date) {
    return timeFromSeconds(
      value.getUTCHours() * 3600
      + value.getUTCMinutes() * 60
      + value.getUTCSeconds(),
    );
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0 || value >= 1) {
      return { error: 'must be an Excel time or HH:mm[:ss].' };
    }
    return timeFromSeconds(Math.round(value * 24 * 60 * 60));
  }
  if (typeof value === 'object' && 'result' in value) {
    return parseTime(value.result as ExcelJS.CellValue);
  }

  const raw = cellToString(value);
  const matched = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/);
  if (!matched) return { error: 'must use HH:mm or HH:mm:ss.' };

  const hours = Number(matched[1]);
  const minutes = Number(matched[2]);
  const seconds = Number(matched[3] ?? 0);
  return timeFromSeconds(hours * 3600 + minutes * 60 + seconds);
};

const decimalPlaces = (value: number): number => {
  const [coefficient, exponentText] = String(value).toLowerCase().split('e');
  const fractionLength = coefficient.split('.')[1]?.length ?? 0;
  return Math.max(0, fractionLength - Number(exponentText ?? 0));
};

const parseCoordinate = (
  raw: string,
  field: 'latitude' | 'longitude',
): { value?: number; error?: string } => {
  const value = Number(raw);
  const limit = field === 'latitude' ? 90 : 180;
  if (!Number.isFinite(value) || Math.abs(value) > limit || decimalPlaces(value) > 6) {
    return {
      error: `${field} must be a number between -${limit} and ${limit} with at most 6 decimal places.`,
    };
  }
  return { value };
};

const parseOpeningHoursJson = (
  raw: string,
): { hours?: VenueOpeningHoursData[]; error?: string } => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { error: 'openingHours must be a JSON array.' };
  }
  if (!Array.isArray(parsed)) {
    return { error: 'openingHours must be a JSON array.' };
  }
  if (parsed.length > 7) {
    return { error: 'openingHours must have at most 7 days.' };
  }

  const hours: VenueOpeningHoursData[] = [];
  const seenDays = new Set<DayOfWeek>();
  const errors: string[] = [];

  parsed.forEach((item, index) => {
    const label = `openingHours[${index}]`;
    if (item == null || typeof item !== 'object' || Array.isArray(item)) {
      errors.push(`${label} must be an object.`);
      return;
    }
    const entry = item as Record<string, unknown>;
    const dayToken = entry.dayOfWeek == null ? '' : toEnumToken(String(entry.dayOfWeek));
    const dayOfWeek = Object.values(DayOfWeek).includes(dayToken as DayOfWeek)
      ? dayToken as DayOfWeek
      : undefined;
    if (!dayOfWeek) {
      errors.push(`${label} has invalid dayOfWeek.`);
      return;
    }
    if (seenDays.has(dayOfWeek)) {
      errors.push(`${label} duplicates dayOfWeek ${dayOfWeek}.`);
      return;
    }
    seenDays.add(dayOfWeek);

    const closed = typeof entry.closed === 'boolean'
      ? entry.closed
      : parseClosed(entry.closed as ExcelJS.CellValue);
    if (closed == null) {
      errors.push(`${label} has invalid closed; use true or false.`);
      return;
    }

    if (closed) {
      if ((entry.opensAt != null && entry.opensAt !== '')
        || (entry.closesAt != null && entry.closesAt !== '')) {
        errors.push(`${label}: closed days must leave opensAt and closesAt empty.`);
        return;
      }
      hours.push({
        dayOfWeek,
        closed: true,
        opensAt: null,
        closesAt: null,
      });
      return;
    }

    const opensAt = parseTime(entry.opensAt as ExcelJS.CellValue);
    const closesAt = parseTime(entry.closesAt as ExcelJS.CellValue);
    if (entry.opensAt == null || entry.opensAt === '' || opensAt.error) {
      errors.push(`${label}: opensAt ${opensAt.error ?? 'is required.'}`);
    }
    if (entry.closesAt == null || entry.closesAt === '' || closesAt.error) {
      errors.push(`${label}: closesAt ${closesAt.error ?? 'is required.'}`);
    }
    if (opensAt.seconds != null
      && closesAt.seconds != null
      && opensAt.seconds >= closesAt.seconds) {
      errors.push(`${label}: opensAt must be before closesAt.`);
    }
    if (opensAt.value && closesAt.value
      && (opensAt.seconds == null || closesAt.seconds == null
        || opensAt.seconds < closesAt.seconds)) {
      hours.push({
        dayOfWeek,
        closed: false,
        opensAt: opensAt.value,
        closesAt: closesAt.value,
      });
    }
  });

  if (errors.length > 0) return { error: errors.join(' ') };
  return { hours };
};

const assignOptionalValues = (
  venue: CreateVenueReq,
  values: Partial<Record<keyof CreateVenueReq, string>>,
) => {
  if (values.venueTag) venue.venueTag = values.venueTag;
  if (values.nameEn) venue.nameEn = values.nameEn;
  if (values.district) venue.district = values.district;
  if (values.roadAddressEn) venue.roadAddressEn = values.roadAddressEn;
  if (values.instagramUrl) venue.instagramUrl = values.instagramUrl;
  if (values.websiteUrl) venue.websiteUrl = values.websiteUrl;
  if (values.phoneNumber) venue.phoneNumber = values.phoneNumber;
  if (values.naverMapUrl) venue.naverMapUrl = values.naverMapUrl;
  if (values.googleMapUrl) venue.googleMapUrl = values.googleMapUrl;
  if (values.admissionNote) venue.admissionNote = values.admissionNote;
  if (values.openingHoursNote) venue.openingHoursNote = values.openingHoursNote;
};

const invalidResult = (
  rowNumber: number,
  venueCode: string | undefined,
  errors: string[],
): CreateVenueBulkItemResult => ({
  rowNumber,
  venueCode: venueCode || null,
  status: 'INVALID',
  venueId: null,
  message: errors.join(' '),
});

interface ParsedVenueRow {
  rowNumber: number;
  venueCode?: string;
  venue?: CreateVenueReq;
  openingHours?: VenueOpeningHoursData[];
  errors: string[];
}

export const parseVenueBulkSheet = async (
  file: File,
): Promise<VenueBulkSheetParseResult> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets.find(
    (candidate) => normalizeHeader(candidate.name) === 'venues',
  ) ?? workbook.worksheets[0];
  if (!sheet) {
    throw new VenueBulkSheetParseError('NO_SHEET', 'The workbook has no sheet.');
  }

  const { columnKeys, colCount } = getColumnKeys(
    sheet,
    HEADER_MAP,
    REQUIRED_HEADERS,
  );
  const venueRows: ParsedVenueRow[] = [];

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;

    const values: Partial<Record<keyof CreateVenueReq, string>> = {};
    const numericIdentifiers: Array<'venueCode' | 'phoneNumber'> = [];
    const cellErrors: string[] = [];
    let openingHoursRaw = '';
    let hasData = false;

    for (let col = 1; col <= colCount; col += 1) {
      const key = columnKeys[col];
      if (!key) continue;
      const cellValue = row.getCell(col).value;
      if (hasCellData(cellValue)) hasData = true;
      if (key === 'openingHours') {
        openingHoursRaw = cellToString(cellValue);
        const cellError = getCellError(cellValue);
        if (cellError) cellErrors.push(`${key}: ${cellError}.`);
        continue;
      }
      const text = cellToString(cellValue, URL_FIELDS.has(key));
      if (text) values[key] = text;
      const cellError = getCellError(cellValue);
      if (cellError) cellErrors.push(`${key}: ${cellError}.`);
      if ((key === 'venueCode' || key === 'phoneNumber')
        && isNumericCell(cellValue)) {
        numericIdentifiers.push(key);
      }
    }

    if (!hasData) return;

    const errors: string[] = [...cellErrors];
    const missingFields = REQUIRED_HEADERS.filter((key) => !values[key]);
    if (missingFields.length > 0) {
      errors.push(`Missing required fields: ${missingFields.join(', ')}.`);
    }

    numericIdentifiers.forEach((key) => {
      errors.push(`${key} must be stored as text in Excel.`);
    });

    const typeToken = values.type ? toEnumToken(values.type) : '';
    const type = Object.values(VenueType).includes(typeToken as VenueType)
      ? typeToken as VenueType
      : undefined;
    if (values.type && !type) {
      errors.push(`Invalid type "${values.type}".`);
    }

    const admissionTypeToken = values.admissionType
      ? toEnumToken(values.admissionType)
      : '';
    const admissionType = Object.values(VenueAdmissionType).includes(
      admissionTypeToken as VenueAdmissionType,
    )
      ? admissionTypeToken as VenueAdmissionType
      : undefined;
    if (values.admissionType && !admissionType) {
      errors.push(`Invalid admissionType "${values.admissionType}".`);
    }

    const hasLatitude = !!values.latitude;
    const hasLongitude = !!values.longitude;
    let latitude: number | undefined;
    let longitude: number | undefined;
    if (hasLatitude !== hasLongitude) {
      errors.push('latitude and longitude must both be provided.');
    } else if (hasLatitude && hasLongitude) {
      const parsedLatitude = parseCoordinate(values.latitude!, 'latitude');
      const parsedLongitude = parseCoordinate(values.longitude!, 'longitude');
      if (parsedLatitude.error) errors.push(parsedLatitude.error);
      if (parsedLongitude.error) errors.push(parsedLongitude.error);
      latitude = parsedLatitude.value;
      longitude = parsedLongitude.value;
    }

    let columnOpeningHours: VenueOpeningHoursData[] | undefined;
    if (openingHoursRaw) {
      const parsedHours = parseOpeningHoursJson(openingHoursRaw);
      if (parsedHours.error) errors.push(parsedHours.error);
      else columnOpeningHours = parsedHours.hours;
    }

    let venue: CreateVenueReq | undefined;
    if (errors.length === 0) {
      venue = {
        venueCode: values.venueCode!,
        name: values.name!,
        type: type!,
        countryCode: values.countryCode!,
        city: values.city!,
        roadAddress: values.roadAddress!,
        timeZone: values.timeZone!,
      };
      if (latitude != null && longitude != null) {
        venue.latitude = latitude;
        venue.longitude = longitude;
      }
      if (admissionType) venue.admissionType = admissionType;
      assignOptionalValues(venue, values);
    }

    venueRows.push({
      rowNumber,
      venueCode: values.venueCode,
      venue,
      openingHours: columnOpeningHours,
      errors,
    });
  });

  const venueRowsByCode = new Map<string, ParsedVenueRow[]>();
  venueRows.forEach((venueRow) => {
    if (!venueRow.venueCode) return;
    const code = normalizeVenueCode(venueRow.venueCode);
    venueRowsByCode.set(code, [
      ...(venueRowsByCode.get(code) ?? []),
      venueRow,
    ]);
  });

  const openingHoursByCode = new Map<string, VenueOpeningHoursData[]>();
  const openingHoursSheet = workbook.worksheets.find(
    (candidate) => normalizeHeader(candidate.name) === 'openinghours',
  );

  if (openingHoursSheet && openingHoursSheet !== sheet) {
    const {
      columnKeys: openingColumnKeys,
      colCount: openingColCount,
    } = getColumnKeys(
      openingHoursSheet,
      OPENING_HOURS_HEADER_MAP,
      REQUIRED_OPENING_HOURS_HEADERS,
    );
    const seenDaysByCode = new Map<string, Set<DayOfWeek>>();

    openingHoursSheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return;

      const rawValues: Partial<Record<OpeningHoursColumn, ExcelJS.CellValue>> = {};
      const values: Partial<Record<OpeningHoursColumn, string>> = {};
      const errors: string[] = [];
      let hasData = false;

      for (let col = 1; col <= openingColCount; col += 1) {
        const key = openingColumnKeys[col];
        if (!key) continue;
        const cellValue = row.getCell(col).value;
        rawValues[key] = cellValue;
        if (hasCellData(cellValue)) hasData = true;
        const text = cellToString(cellValue);
        if (text) values[key] = text;
        const cellError = getCellError(cellValue);
        if (cellError) errors.push(`${key}: ${cellError}.`);
      }

      if (!hasData) return;
      if (!values.venueCode) {
        throw new VenueBulkSheetParseError(
          'UNKNOWN_VENUE_CODE',
          `OpeningHours row ${rowNumber} has no venueCode.`,
        );
      }

      const code = normalizeVenueCode(values.venueCode);
      const targets = venueRowsByCode.get(code);
      if (!targets) {
        throw new VenueBulkSheetParseError(
          'UNKNOWN_VENUE_CODE',
          `OpeningHours row ${rowNumber} references unknown venueCode "${values.venueCode}".`,
        );
      }

      if (isNumericCell(rawValues.venueCode!)) {
        errors.push('venueCode must be stored as text in Excel.');
      }
      if (!values.dayOfWeek) errors.push('Missing required field: dayOfWeek.');
      if (!hasCellData(rawValues.closed)) errors.push('Missing required field: closed.');

      const dayToken = values.dayOfWeek ? toEnumToken(values.dayOfWeek) : '';
      const dayOfWeek = Object.values(DayOfWeek).includes(dayToken as DayOfWeek)
        ? dayToken as DayOfWeek
        : undefined;
      if (values.dayOfWeek && !dayOfWeek) {
        errors.push(`Invalid dayOfWeek "${values.dayOfWeek}".`);
      }

      if (dayOfWeek) {
        const seenDays = seenDaysByCode.get(code) ?? new Set<DayOfWeek>();
        if (seenDays.has(dayOfWeek)) {
          errors.push(`Duplicate dayOfWeek ${dayOfWeek}.`);
        } else {
          seenDays.add(dayOfWeek);
          seenDaysByCode.set(code, seenDays);
        }
      }

      const closed = parseClosed(rawValues.closed!);
      if (hasCellData(rawValues.closed) && closed == null) {
        errors.push(`Invalid closed value "${values.closed ?? ''}"; use TRUE or FALSE.`);
      }

      let opensAt: ParsedTime = {};
      let closesAt: ParsedTime = {};
      if (closed === true) {
        if (hasCellData(rawValues.opensAt) || hasCellData(rawValues.closesAt)) {
          errors.push('Closed days must leave opensAt and closesAt empty.');
        }
      } else if (closed === false) {
        if (!hasCellData(rawValues.opensAt) || !hasCellData(rawValues.closesAt)) {
          errors.push('Open days must provide both opensAt and closesAt.');
        } else {
          opensAt = parseTime(rawValues.opensAt!);
          closesAt = parseTime(rawValues.closesAt!);
          if (opensAt.error) errors.push(`opensAt ${opensAt.error}`);
          if (closesAt.error) errors.push(`closesAt ${closesAt.error}`);
          if (opensAt.seconds != null
            && closesAt.seconds != null
            && opensAt.seconds >= closesAt.seconds) {
            errors.push('opensAt must be before closesAt.');
          }
        }
      }

      if (errors.length > 0) {
        const message = `OpeningHours row ${rowNumber}: ${errors.join(' ')}`;
        targets.forEach((target) => target.errors.push(message));
        return;
      }

      if (!dayOfWeek || closed == null) return;
      const openingHour: VenueOpeningHoursData = closed
        ? {
          dayOfWeek,
          closed: true,
          opensAt: null,
          closesAt: null,
        }
        : {
          dayOfWeek,
          closed: false,
          opensAt: opensAt.value!,
          closesAt: closesAt.value!,
        };
      openingHoursByCode.set(code, [
        ...(openingHoursByCode.get(code) ?? []),
        openingHour,
      ]);
    });
  }

  const venueList: CreateVenueBulkItemReq[] = [];
  const invalidResults: CreateVenueBulkItemResult[] = [];
  venueRows.forEach((venueRow) => {
    if (venueRow.errors.length > 0 || !venueRow.venue) {
      invalidResults.push(invalidResult(
        venueRow.rowNumber,
        venueRow.venueCode,
        venueRow.errors,
      ));
      return;
    }

    const openingHoursFromSheet = openingHoursByCode.get(
      normalizeVenueCode(venueRow.venue.venueCode),
    );
    if (venueRow.openingHours?.length && openingHoursFromSheet?.length) {
      invalidResults.push(invalidResult(
        venueRow.rowNumber,
        venueRow.venueCode,
        ['opening hours provided in both openingHours column and OpeningHours sheet.'],
      ));
      return;
    }
    const openingHours = venueRow.openingHours?.length
      ? venueRow.openingHours
      : openingHoursFromSheet;
    if (openingHours?.length) venueRow.venue.openingHours = openingHours;
    venueList.push({ rowNumber: venueRow.rowNumber, venue: venueRow.venue });
  });

  return {
    venueList,
    invalidResults,
    totalCount: venueRows.length,
  };
};
